import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import apiClient from '../api/apiClient';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuthStatus = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/api/users/profile');
      const profile = response.data?.user;
      if (profile) {
        setUser({ ...profile, authType: 'email' });
        localStorage.setItem('user', JSON.stringify(profile));
        setIsLoading(false);
        return;
      }
      setUser(null);
      localStorage.removeItem('user');
    } catch (error) {
      setUser(null);
      localStorage.removeItem('user');
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthStatus();
  }, [checkAuthStatus]);

  const login = (userData) => {
    setUser({ ...userData, authType: 'email' });
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const logout = async () => {
    console.log('Logging out user...');
    
    try {
      await apiClient.post('/api/users/logout');
    } catch (error) {
      // proceed to clear client state even if server call fails
    } finally {
      sessionStorage.removeItem('authToken');
      localStorage.removeItem('authToken');
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      // Best-effort clear for CSRF/session cookies that are not HttpOnly
      document.cookie = 'csrf_token=; Max-Age=0; path=/;';
      document.cookie = 'auth_token=; Max-Age=0; path=/;';
      setUser(null);
      console.log('User logged out successfully');
    }
  };

  const isAuthenticated = user !== null;

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    checkAuthStatus
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
